<template>
  <div class="wt2-dropdown" :class="type">
    <button
      class="wt2-dropdown-button"
      :class="buttonClass"
      :disabled="disabled"
      @click="showDropdownMenu = !showDropdownMenu"
      ref="button"
    >
      <slot name="button" :current-item="currentItem">
        <!-- fallback content here -->
        <span v-if="label" class="wt2-dropdown-label">{{ label }}:</span>
        <span
          v-if="icon"
          class="wt2-dropdown-icon"
          :class="['wticon', icon]"
        ></span>
        <span class="text-three-dots">{{ currentItem.label }}</span>
        <i
          v-show="!label || showTriangle"
          class="wticon wticon-chevron-down -chevron"
        ></i>
      </slot>
    </button>

    <ul
      v-show="showDropdownMenu"
      class="wt2-dropdown-menu"
      :style="listStyle"
      ref="dropdown"
    >
      <li
        v-for="item in items"
        :key="item.id"
        class="wt2-dropdown-menu-item wt2-text-normal text-three-dots"
        :class="{
          selected: item.id === computedItemId,
          disabled: item.disabled,
        }"
        :style="itemStyle"
        :data-id="item.id"
        @click="clickItem(item)"
      >
        <slot name="item" :item="item">
          <!-- fallback content here -->
          <span :title="item.label">{{ item.label }}</span>
        </slot>
      </li>
    </ul>
  </div>
</template>

<script>
import app from 'app';

export default {
  props: {
    items: { type: Array, required: true },
    itemHeight: { type: Number, default: 50 },
    numItems: { type: Number, default: 5 },
    currentItemId: { type: [String, Number] },
    onClickItem: { type: Function, required: true },
    type: { type: String, default: 'underline fixed' },
    disabled: { type: Boolean, default: false },
    label: { type: String, default: '' },
    icon: { type: String },
    showTriangle: { type: Boolean, default: false },
    cancelableSelection: { type: Boolean, default: false },
  },

  mounted() {
    document.addEventListener('click', this.$_clickOutside);
    this.activeItemId = this.currentItemId;
    this.$_handleKeydown = this.$_handleKeydown.bind(this);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.$_clickOutside);
    document.removeEventListener('keydown', this.$_handleKeydown);
    app.keyboardAccessibility.remove(this.guid);
    clearTimeout(this.searchTimeout);
  },

  data() {
    return {
      guid: app.guid(),
      showDropdownMenu: false,
      activeItemId: null,
      originalItemId: null,
      searchText: '',
      searchTimeout: null,
      kaHandlers: [
        {
          on: 'keydown',
          key: $.ui.keyCode.ESCAPE,
          run: () => {
            this.showDropdownMenu = false;
          },
        },
        {
          on: 'keydown',
          key: $.ui.keyCode.ENTER,
          run: (e) => {
            if (this.activeItemId) {
              const item = this.items.find((i) => i.id === this.activeItemId);
              if (item) {
                e.preventDefault();
                e.stopPropagation();
                this.clickItem(item);
              }
            }
          },
        },
        {
          on: 'keydown',
          key: $.ui.keyCode.UP,
          run: () => {
            const idx = this.items.findIndex((i) => i.id === this.activeItemId);
            const prevItem =
              this.items[idx > 0 ? idx - 1 : this.items.length - 1];
            if (prevItem) {
              this.activeItemId = prevItem.id;
              this.$refs.dropdown
                .querySelector(`[data-id="${prevItem.id}"]`)
                ?.scrollIntoView({ block: 'nearest' });
            }
          },
        },
        {
          on: 'keydown',
          key: $.ui.keyCode.DOWN,
          run: () => {
            const idx = this.items.findIndex((i) => i.id === this.activeItemId);
            const nextItem =
              this.items[idx < this.items.length - 1 ? idx + 1 : 0];
            if (nextItem) {
              this.activeItemId = nextItem.id;
              this.$refs.dropdown
                .querySelector(`[data-id="${nextItem.id}"]`)
                ?.scrollIntoView({ block: 'nearest' });
            }
          },
        },
      ],
    };
  },

  watch: {
    currentItemId(newVal) {
      this.activeItemId = newVal;
    },

    showDropdownMenu() {
      const dropdownEl = this.$refs.dropdown;
      // const buttonEl = this.$refs.button;
      if (this.showDropdownMenu) {
        this.originalItemId = this.activeItemId;
        app.keyboardAccessibility.add(this.guid, this.kaHandlers);
        document.addEventListener('keydown', this.$_handleKeydown);
        this.$nextTick(() => {
          const dropdownRect = dropdownEl.getBoundingClientRect();
          const dropdownBottom = dropdownRect.top + dropdownRect.height;
          const windowBottom = window.innerHeight;
          // const buttonRect = buttonEl.getBoundingClientRect();
          if (dropdownBottom > windowBottom) {
            dropdownEl.style.top = `${windowBottom - dropdownBottom}px`;
          }

          // Scroll to current item
          if (this.activeItemId) {
            const currentEl = dropdownEl.querySelector(
              `[data-id="${this.activeItemId}"]`
            );
            currentEl?.scrollIntoView({ block: 'center', behavior: 'auto' });
          }
        });
      } else {
        app.keyboardAccessibility.remove(this.guid);
        document.removeEventListener('keydown', this.$_handleKeydown);
        dropdownEl.style.top = 'inherit';
        this.searchText = '';
        clearTimeout(this.searchTimeout);
        this.activeItemId = this.originalItemId;
      }
      this.$emit('onShowHide', this.showDropdownMenu);
    },
  },

  computed: {
    buttonClass() {
      return {
        'has-label': !!this.label,
        'has-icon': !!this.icon,
        'dropdown-menu-open': this.showDropdownMenu,
      };
    },
    currentItem() {
      return this.items.find((item) => item.id === this.computedItemId) || {};
    },

    computedItemId() {
      return this.activeItemId;
    },

    itemStyle() {
      return {
        height: `${this.itemHeight}px`,
      };
    },

    listStyle() {
      const height = this.itemHeight * this.numItems;
      return {
        maxHeight: `${height}px`,
      };
    },
  },

  methods: {
    $_clickOutside(e) {
      const isAttached = document.body.contains(e.target);
      const isInside = this.$el.contains(e.target);
      if (isAttached && !isInside) {
        this.showDropdownMenu = false;
      }
    },

    $_handleKeydown(e) {
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        clearTimeout(this.searchTimeout);
        this.searchText += e.key.toLowerCase();

        const match = this.items.find((item) =>
          item.label.toLowerCase().startsWith(this.searchText)
        );

        if (match) {
          const el = this.$refs.dropdown.querySelector(
            `[data-id="${match.id}"]`
          );
          el?.scrollIntoView({ block: 'center' });
          this.activeItemId = match.id;
        }

        this.searchTimeout = setTimeout(() => (this.searchText = ''), 500);
        e.preventDefault();
      }
    },

    clickItem(item, event) {
      this.onClickItem(item.id, item, event);
      this.originalItemId = item.id;
      this.showDropdownMenu = false;
      if (!this.cancelableSelection && _.isDefined(item.label)) {
        this.activeItemId = item.id;
      }
    },
  },
};
</script>
